import tw from 'twin.macro';
import React, { useEffect } from 'react';
import { getChoices } from './utils';
import MultiOptionsCreatable from '@teetsh/app/src/components/Form/MultiOptionsCreatable';
import BoListComponent from './BoListComponent';
import { BOLabels, Item } from '@teetsh/app/src/types/user';
import {
  ArrowLeftIcon,
  LightBulbIcon,
  QuestionMarkCircleIcon as QuestionMarkCircle,
} from '@heroicons/react/24/outline';
import { Bo } from '@teetsh/app/src/types/bo';
import { CycleSelector } from '@teetsh/app/src/components/Bo/CycleSelector';
import ButtonExternal from '@teetsh/app/src/components/Buttons/ExternalLink';
import {
  Cycle,
  DEFAULT_CYCLE,
  NiveauValue,
} from '@teetsh/app/src/containers/scholarSystem';
import { tutoUrls } from '@teetsh/app/src/containers/tutoUrls';
import { useTranslation } from 'react-i18next';
import { getGender } from '@teetsh/app/src/utils/getGender';
import { useBoSuggestion } from './useBoSuggestion';
import { useBoUsageHint } from './useBoUsageHint';
import { Suggestion, SuggestionEntity, BoValues } from './types';
import { useBoSuggestionTracking } from './useBoSuggestionTracking';
import _isEmpty from 'lodash-es/isEmpty';
import { isReferentielValuesEmpty } from './isReferentielValuesEmpty';

function isEmptySuggestion(suggestion?: Suggestion) {
  if (!suggestion) {
    return true;
  }
  return (
    _isEmpty(suggestion.matiere) &&
    (suggestion?.domaines || []).length === 0 &&
    (suggestion?.competences || []).length === 0 &&
    (suggestion?.objectifs || []).length === 0
  );
}

const SuggestionsAvailableForBOs = [
  // Programmes 2015-2024 France
  'e194a0e9-fde3-4196-9c58-9f549c6cf27e',
  // Programmes 2025 France (currently not available)
  // '07058b28-732a-4e44-a490-8ea933718555',
];

export interface Props {
  cycle?: string;
  skipBoSuggestion?: boolean;
  fallbackCycle?: string;
  hideHeader?: boolean;
  boLabels: BOLabels;
  bo: Bo;
  suggestionEntity?: SuggestionEntity;
  matiereSingle?: boolean;
  domaineSingle?: boolean;
  competenceSingle?: boolean;
  goalSingle?: boolean;
  values: BoValues;
  onChange: (v: BoValues) => void;
  matierePlaceholder?: string;
  children?: React.ReactNode;
  noBoList?: boolean;
  onUpdateCycle?: (cycle: Cycle | NiveauValue) => void;
  onUpdateReferentiel: (id: string) => void;
  guestMode?: boolean;
  boList: Bo[];
  suggestionParams?: {
    title: string;
    tag?: string;
  };
}

function getCycleFromBo(bo: Bo, cycle: string, fallbackCycle?: string): string {
  const matchingCycle = (bo.cycles || []).find(c => c.nom === cycle);
  if (matchingCycle) {
    return matchingCycle?.nom;
  }
  if ((bo.cycles || []).length > 0) {
    if (fallbackCycle) {
      const matchingDefaultCycle = (bo.cycles || []).find(
        c => c.nom === fallbackCycle,
      );
      if (matchingDefaultCycle) {
        return matchingDefaultCycle?.nom;
      }
    }
    return bo.cycles[0].nom;
  }
  return cycle;
}

export function BO(props: Props) {
  const {
    bo,
    values,
    onChange,
    matiereSingle,
    domaineSingle,
    competenceSingle,
    goalSingle,
    children,
    boLabels,
    suggestionParams,
    suggestionEntity,
    hideHeader,
  } = props;
  const { t } = useTranslation(['referentiel', 'common']);
  // skip is for the admin. It won't change once the component is mounted
  const skip = props.skipBoSuggestion ?? false;
  // Whereas enableBoSuggestions is for the app, where the selected BO can change dynamically
  const enableBoSuggestions =
    bo.isOfficial && SuggestionsAvailableForBOs.includes(bo.id);
  const { onSuggestionsClick, onSuggestions } = useBoSuggestionTracking();
  const cycle = getCycleFromBo(
    bo,
    values.cycle || props.cycle || DEFAULT_CYCLE,
    props.fallbackCycle,
  );
  const matieres = (bo.cycles || []).find(c => c.nom === cycle)?.matieres || [];
  const { domaines, competences, objectifs } = getChoices(
    matieres,
    (values.matiere || []).map(m => m.value),
    (values.domaine || []).map(m => m.value),
    (values.competence || []).map(m => m.value),
    (values.goals || []).map(m => m.value),
  );
  const treeVerticalStyle = tw`ml-5 -mt-0.5 pl-0 border-l border-neutral-moderate relative`;
  const treeHorizontalStyle = tw`-ml-[1px] w-4 h-4 rounded-bl-xl border-b border-l border-neutral-moderate inline-block absolute left-0 top-0`;

  const { data: suggestions } = useBoSuggestion({
    title: suggestionParams?.title,
    cycle,
    entity: suggestionEntity || 'cj',
    skip,
    enabled: enableBoSuggestions,
  });
  const suggestion: Suggestion =
    suggestions && (suggestions || []).length > 0
      ? suggestions[0]
      : {
          matiere: '',
          domaines: [],
          competences: [],
          objectifs: [],
        };
  useEffect(() => {
    const hasASuggestionBeenShown =
      ((values.matiere || []).length === 0 && suggestion?.matiere !== '') ||
      ((values.domaine || []).length === 0 &&
        (suggestion?.domaines || []).length > 0) ||
      ((values.competence || []).length === 0 &&
        (suggestion?.competences || []).length > 0) ||
      ((values.goals || []).length === 0 &&
        (suggestion?.objectifs || []).length > 0);
    if (
      (suggestions || []).length > 0 &&
      !isEmptySuggestion(suggestions?.[0]) &&
      hasASuggestionBeenShown
    ) {
      onSuggestions(suggestions || []);
    }
  }, [
    JSON.stringify(suggestions),
    // If you have suggestion that are not shown, but you then remove a
    // referential element, the suggestion will be shown again
    // thus we need to refresh on referential elements changes
    (values?.matiere || []).length,
    (values?.domaine || []).length,
    (values?.competence || []).length,
    (values?.goals || []).length,
  ]);
  const [isBoHintVisible, incrementBoUsage] = useBoUsageHint();

  // N'enlève pas le testid sinon pour les tests ça va être galère ⚠
  return (
    <div data-testid="bo-component" tw="flex flex-col gap-4">
      {!hideHeader && (
        <div tw="flex gap-2 flex-col md:flex-row justify-between">
          <div tw="flex items-center gap-2">
            <h4 tw="text-neutral-moderate">
              {t('referentiel:boSelector.title')}
            </h4>
            <ButtonExternal
              href={tutoUrls.referentiels}
              icon={QuestionMarkCircle}
              minimal
              small
            />
          </div>
          <div tw="flex items-center gap-2">
            {props.boList.length > 0 && (
              <div>
                <BoListComponent
                  boList={props.boList}
                  onUpdateReferentiel={props.onUpdateReferentiel}
                  defaultReferentielId={bo.id}
                  guestMode={props.guestMode}
                  minimal={false}
                />
              </div>
            )}
            <div>
              <CycleSelector
                onChange={cy => {
                  onChange({ ...values, cycle: cy });
                  if (props.onUpdateCycle) {
                    props.onUpdateCycle(cy);
                  }
                }}
                cycle={cycle}
                bo={bo}
              />
            </div>
          </div>
        </div>
      )}
      <div>
        <div tw="md:flex flex-col">
          {hideHeader && (
            <div tw="flex mb-2 gap-2">
              {props.boList.length > 0 && (
                <div>
                  <BoListComponent
                    boList={props.boList}
                    onUpdateReferentiel={props.onUpdateReferentiel}
                    defaultReferentielId={bo.id}
                    guestMode={props.guestMode}
                    minimal={false}
                  />
                </div>
              )}
              <div>
                <CycleSelector
                  onChange={cy => {
                    onChange({ ...values, cycle: cy });
                    if (props.onUpdateCycle) {
                      props.onUpdateCycle(cy);
                    }
                  }}
                  cycle={cycle}
                  bo={bo}
                />
              </div>
            </div>
          )}
          <div tw="flex-grow">
            <div>
              <MultiOptionsCreatable
                id="matiere"
                items={values.matiere || []}
                singleValue={matiereSingle}
                onChange={(matiere: Item[]) => {
                  if (matiere.some(m => m.value !== '') && isBoHintVisible) {
                    incrementBoUsage();
                  }
                  onChange({ ...values, matiere });
                }}
                label={
                  (values.matiere || []).length > 1
                    ? t(`common:labels.${boLabels.matierePluralName}`, {
                        defaultValue: boLabels.matierePluralName,
                      })
                    : t(`common:labels.${boLabels.matiereSingularName}`, {
                        defaultValue: boLabels.matiereSingularName,
                      })
                }
                hint={
                  isBoHintVisible && (
                    <div tw="hidden sm:flex text-neutral-quiet text-sm items-center gap-1">
                      <ArrowLeftIcon tw="inline-block h-4 w-4" />
                      {t('common:components.bo.boUsageHint')}
                      <LightBulbIcon tw="inline-block h-4 w-4" />
                    </div>
                  )
                }
                displayLength
                options={(matieres || [])
                  .filter(
                    m =>
                      !(values.matiere || []).map(n => n.value).includes(m.nom),
                  )
                  .map(m => ({
                    label: m.nom,
                    value: m.nom,
                  }))}
                addButtonLabel={t('common:labels.addX', {
                  context: getGender(boLabels?.matiereSingularName),
                  label: t(`common:labels.${boLabels.matiereSingularName}`, {
                    defaultValue: boLabels.matiereSingularName,
                  }),
                })}
                onSuggestionClick={(v: string) => {
                  if (onSuggestionsClick) {
                    onSuggestionsClick?.({
                      matiere: {
                        input: suggestionParams?.title || '',
                        valueClicked: v,
                      },
                    });
                  }
                }}
                suggestion={suggestion.matiere}
              >
                {children}
              </MultiOptionsCreatable>
            </div>
          </div>
        </div>
        <div
          css={[
            tw`pt-2 pb-2`,
            treeVerticalStyle,
            (values.domaine || []).length > 0 && tw`pl-0.5`,
          ]}
        >
          <span css={[treeHorizontalStyle, tw`top-2`]} />
          <div css={[tw`ml-4`, (values.domaine || []).length > 0 && tw`pt-1`]}>
            <MultiOptionsCreatable
              id="domaines"
              items={values.domaine}
              onChange={(domaine: Item[]) => onChange({ ...values, domaine })}
              singleValue={domaineSingle}
              label={
                (values.domaine || []).length > 1
                  ? t(`common:labels.${boLabels.domainePluralName}`, {
                      defaultValue: boLabels.domainePluralName,
                    })
                  : t(`common:labels.${boLabels.domaineSingularName}`, {
                      defaultValue: boLabels.domaineSingularName,
                    })
              }
              displayLength
              options={domaines.map(d => ({
                label: d,
                value: d,
              }))}
              addButtonLabel={t('common:labels.addX', {
                context: getGender(boLabels?.domaineSingularName),
                label: t(`common:labels.${boLabels.domaineSingularName}`, {
                  defaultValue: boLabels.domaineSingularName,
                }),
              })}
              onSuggestionClick={(v: string) => {
                if (onSuggestionsClick) {
                  onSuggestionsClick?.({
                    domaine: {
                      input: suggestionParams?.title || '',
                      valueClicked: v,
                    },
                  });
                }
              }}
              suggestion={
                ((suggestion?.domaines || []).length > 0 &&
                  suggestion.domaines[0]) ||
                ''
              }
            >
              {children}
            </MultiOptionsCreatable>
          </div>
        </div>
        <div
          css={[
            tw`pb-2`,
            treeVerticalStyle,
            (values.competence || []).length > 0 && tw`pl-0.5`,
          ]}
        >
          <span css={[treeHorizontalStyle]} />
          <div
            css={[tw`ml-4`, (values.competence || []).length > 0 && tw`pt-1`]}
          >
            <MultiOptionsCreatable
              id="competence"
              items={values.competence}
              onChange={(competence: Item[]) =>
                onChange({ ...values, competence })
              }
              label={
                (values.competence || []).length > 1
                  ? t(`common:labels.${boLabels.competencePluralName}`, {
                      defaultValue: boLabels.competencePluralName,
                    })
                  : t(`common:labels.${boLabels.competenceSingularName}`, {
                      defaultValue: boLabels.competenceSingularName,
                    })
              }
              singleValue={competenceSingle}
              displayLength
              options={competences.map(d => ({
                label: d,
                value: d,
              }))}
              addButtonLabel={t('common:labels.addX', {
                context: getGender(boLabels?.competenceSingularName),
                label: t(`common:labels.${boLabels.competenceSingularName}`, {
                  defaultValue: boLabels.competenceSingularName,
                }),
              })}
              onSuggestionClick={(v: string) => {
                if (onSuggestionsClick) {
                  onSuggestionsClick?.({
                    competence: {
                      input: suggestionParams?.title || '',
                      valueClicked: v,
                    },
                  });
                }
              }}
              suggestion={
                ((suggestion?.competences || []).length > 0 &&
                  suggestion.competences[0]) ||
                ''
              }
            >
              {children}
            </MultiOptionsCreatable>
          </div>
        </div>

        <div
          data-testid="goals"
          css={[
            treeVerticalStyle,
            tw`border-transparent`,
            (values.goals || []).length > 0 && tw`pl-0.5`,
          ]}
        >
          <span css={[treeHorizontalStyle]} />
          <div css={[tw`ml-4`, (values.goals || []).length > 0 && tw`pt-1`]}>
            <MultiOptionsCreatable
              id="goals"
              items={values.goals}
              onChange={(goals: Item[]) => onChange({ ...values, goals })}
              singleValue={goalSingle}
              label={
                (values.goals || []).length > 1
                  ? t(`common:labels.${boLabels.objectifPluralName}`, {
                      defaultValue: boLabels.objectifPluralName,
                    })
                  : t(`common:labels.${boLabels.objectifSingularName}`, {
                      defaultValue: boLabels.objectifSingularName,
                    })
              }
              displayLength
              addButtonLabel={t('common:labels.addX', {
                context: getGender(boLabels?.objectifSingularName),
                label: t(`common:labels.${boLabels.objectifSingularName}`, {
                  defaultValue: boLabels.objectifSingularName,
                }),
              })}
              suggestion={
                ((suggestion?.objectifs || []).length > 0 &&
                  suggestion.objectifs[0]) ||
                ''
              }
              onSuggestionClick={(v: string) => {
                if (onSuggestionsClick) {
                  onSuggestionsClick?.({
                    objectif: {
                      input: suggestionParams?.title || '',
                      valueClicked: v,
                    },
                  });
                }
              }}
              options={objectifs.map(d => ({
                label: d,
                value: d,
              }))}
            >
              {children}
            </MultiOptionsCreatable>
          </div>
        </div>
      </div>
    </div>
  );
}

export { isReferentielValuesEmpty };
export type { BoValues };
